<template>
  <div>
    <template v-if="expeditionsStore.adventurersProgress">
      <!-- Desktop table view -->
      <div class="hidden sm:block border border-gray-200 shadow-sm rounded-xl overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-normal text-gray-500">
                Fellow
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                <div>Completed tasks %</div>
                <div class="text-gray-400">(until current week)</div>
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                <div>Completed tasks</div>
                <div class="text-gray-400">(in current week)</div>
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                Total Points
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                Program completion
                <div class="text-gray-400">(Required Tasks)</div>
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                Backlog
                <div class="text-gray-400">(Required Tasks)</div>
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                Future tasks completed
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                Status
              </th>
              <th scope="col" class="px-6 py-3 text-center text-xs font-normal text-gray-500">
                Warnings
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="user in sortedUsers"
              :key="user.id"
              @click="goToUserView(user.id)"
              class="hover:bg-gray-100 transition-all duration-150 cursor-pointer"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <FellowAvatarDropdown
                    :user-id="user.id"
                    :username="user.username"
                    :avatar-src="user.avatar_image"
                    :name="`${user.first_name} ${user.last_name}`"
                    :curriculum-path="`/manage/${expeditionId}/${user.id}`"
                    :is-blurred="isBlurred"
                  />
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900" :class="{ 'blur-md select-none': isBlurred }">
                      {{ user.first_name }} {{ user.last_name }}
                    </div>
                    <div class="text-sm text-gray-500" :class="{ 'blur-md select-none': isBlurred }">{{ user.email }}</div>
                  </div>
                </div>
              </td>
              <td
                class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center font-semibold"
              >
                {{ user.on_track_percentage || 0 }}%
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                {{ user.current_week_done_tasks_count }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                <img src="/ot_coin.svg" class="inline-block w-4 h-4 mr-0.5" />

                {{ user.total_points.toFixed(1) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                {{ user.done_tasks_count }} / {{ user.total_tasks_count }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                {{ user.left_tasks_count }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                {{ user.ahead_tasks_done_count || 0 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center text-xs">
                <ProgressStatusDisplay
                  :status="user.status"
                  :user-progress="user"
                  :show-details="false"
                  class="px-2 py-0.5 inline-flex leading-5 rounded-full"
                />
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <router-link
                  :to="`/manage/${expeditionId}/reports`"
                  @click.stop
                  class="flex-shrink-0 flex items-center justify-center space-x-1"
                >
                  <Flag
                    v-for="index in user.strike_count || 0"
                    :key="index"
                    class="w-4 h-4 text-yellow-500 hover:scale-110 transition-all duration-150"
                  />
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile card view -->
      <div class="sm:hidden space-y-4">
        <div
          v-for="user in sortedUsers"
          :key="user.id"
          @click="goToUserView(user.id)"
          class="bg-white border border-gray-200 rounded-xl p-4 space-y-4 hover:bg-gray-50 transition-all duration-150 cursor-pointer"
        >
          <div class="flex items-center space-x-4">
            <FellowAvatarDropdown
              :user-id="user.id"
              :username="user.username"
              :avatar-src="user.avatar_image"
              :name="`${user.first_name} ${user.last_name}`"
              :curriculum-path="`/manage/${expeditionId}/${user.id}`"
              :is-blurred="isBlurred"
              size="xl"
            />
            <div>
              <div class="text-base font-medium text-gray-900" :class="{ 'blur-md select-none': isBlurred }">
                {{ user.first_name }} {{ user.last_name }}
              </div>
              <div class="text-sm text-gray-500" :class="{ 'blur-md select-none': isBlurred }">{{ user.email }}</div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 text-sm">
            <div class="space-y-1">
              <div class="text-gray-500">Completed tasks %</div>
              <div class="font-semibold">{{ user.on_track_percentage || 0 }}%</div>
            </div>
            <div class="space-y-1">
              <div class="text-gray-500">Current week tasks</div>
              <div>{{ user.current_week_done_tasks_count }}</div>
            </div>
            <div class="space-y-1">
              <div class="text-gray-500">Total Points</div>
              <div class="flex items-center">
                <img src="/ot_coin.svg" class="w-4 h-4 mr-1" />
                {{ user.total_points.toFixed(1) }}
              </div>
            </div>
            <div class="space-y-1">
              <div class="text-gray-500">Program completion</div>
              <div>{{ user.done_tasks_count }} / {{ user.total_tasks_count }}</div>
            </div>
            <div class="space-y-1">
              <div class="text-gray-500">Backlog</div>
              <div>{{ user.left_tasks_count }}</div>
            </div>
            <div class="space-y-1">
              <div class="text-gray-500">Future tasks</div>
              <div>{{ user.ahead_tasks_done_count || 0 }}</div>
            </div>
          </div>

          <div class="flex items-center justify-between pt-2 border-t border-gray-100">
            <ProgressStatusDisplay
              :status="user.status"
              :user-progress="user"
              :show-details="false"
              class="px-2 py-0.5 inline-flex leading-5 rounded-full text-xs"
            />
            <div class="flex items-center space-x-1">
              <Flag
                v-for="index in user.strike_count || 0"
                :key="index"
                class="w-4 h-4 text-yellow-500"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Loading state -->
    <template v-else>
      <!-- Desktop loading state -->
      <div
        class="hidden sm:block border border-gray-200 shadow-sm mt-4 rounded-xl overflow-hidden"
      >
        <table class="min-w-full divide-y divide-gray-200 animate-pulse">
          <thead class="bg-gray-50">
            <tr>
              <th
                scope="col"
                class="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/4"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/2 mx-auto mb-2"></div>
                <div class="h-3 bg-gray-300 rounded w-1/3 mx-auto"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/2 mx-auto mb-2"></div>
                <div class="h-3 bg-gray-300 rounded w-1/3 mx-auto"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/4 mx-auto"></div>
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider"
              >
                <div class="h-4 bg-gray-300 rounded w-1/4 mx-auto"></div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="n in 5" :key="n">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div class="ml-4 space-y-2">
                    <div class="h-4 bg-gray-200 rounded w-32"></div>
                    <div class="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-12 mx-auto"></div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="h-4 bg-gray-200 rounded w-12 mx-auto"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Flag } from 'lucide-vue-next'
import { useExpeditionsStore } from '@/stores/expeditions'
import ProgressStatusDisplay from '@/components/shared/ProgressStatusDisplay.vue'
import FellowAvatarDropdown from '@/components/common/FellowAvatarDropdown.vue'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const expeditionsStore = useExpeditionsStore()
const route = useRoute()
const router = useRouter()
const expeditionId = route.params.id as string
const { isBlurred } = usePrivacyMode()

const sortedUsers = computed(() => {
  if (!expeditionsStore.adventurersProgress?.result) return []
  return [...expeditionsStore.adventurersProgress.result].sort((a, b) => {
    const percentageA = a.on_track_percentage
    const percentageB = b.on_track_percentage
    return percentageB - percentageA
  })
})

const goToUserView = (userId: number) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({ path: `/manage/${expeditionId}/${userId}` })
}
</script>